import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { saveAnswers, getDrawingQuestions } from "../../store/reducer/drawing/drawingQuestionsSlice";
import { getOpportunityById } from "../../store/reducer/opportunity/opportunitySlice";
import Notes from "../Notes/Notes";
import DrawingTabs from "./DrawingTabs";

function DrawingQuestions() {
  const { id, drawing_id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [opportunityData, setOpportunityData] = useState([]);

  useEffect(() => {
    dispatch(getOpportunityById(id)).then((result) => {
      if (result.payload) {
        setOpportunityData(result.payload);
      }
    });
    dispatch(getDrawingQuestions(drawing_id)).then((result) => {
      if (result.payload) {
        setQuestions(result.payload);
        // Initialize answers from the response
        const initialAnswers = result.payload.reduce((acc, question) => {
          if (question.answer) {
            acc[question.id] = question.answer;
          }
          return acc;
        }, {});
        setAnswers(initialAnswers);
      }
    });
  }, [drawing_id, dispatch]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      answers,
      userId: JSON.parse(localStorage.getItem("userSession"))?.id,
      drawing_id,
    };

    await dispatch(saveAnswers(payload)).then((result) => {
        if (result.payload) {
          toast.success("Answers saved successfully!");
          dispatch(getDrawingQuestions(drawing_id)).then((result) => {
            if (result.payload) {
              const uniqueQuestions = Array.from(
                new Map(result.payload.map((q) => [q.id, q])).values()
              );
              setQuestions(uniqueQuestions);
              const initialAnswers = uniqueQuestions.reduce((acc, question) => {
                if (question.answer) {
                  acc[question.id] = question.answer;
                }
                return acc;
              }, {});
              setAnswers(initialAnswers);
            }
          });
        } else {
          toast.error(result.error.message || "Failed to save answers.");
        }
        setIsSubmitting(false);
    });
  };

  return (
    <>
      <div className="grid grid-cols-3 items-center mb-[30px]">
        <div className="flex gap-[16px] items-center">
          <Link to={`/opportunity/${id}`}>
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              Questions
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              Drawing Questions
            </span>
          </div>
        </div>
        <div className="font-Inter font-[500] leading-[30px] text-[18px] text-center">
          {opportunityData.id}-{drawing_id}-{opportunityData.opportunity_name}
        </div>
        <Notes type="drawing-commercialinvoice-history" drawing_id={drawing_id} />
      </div>

      <DrawingTabs />
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {questions.map((question) => (
            <div
              key={question.id}
              className="bg-white rounded-[12px] p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="grid md:grid-cols-2 gap-4 items-center">
                <label
                  className="font-Inter font-[600] text-[16px] text-[#1C1C20] leading-[24px]"
                  htmlFor={`answer-${question.id}`}
                >
                  {question.question_text}
                </label>
                <div>
                  {question.response_type === "text" && (
                    <input
                      type="text"
                      id={`answer-${question.id}`}
                      value={answers[question.id] || ""}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      className="block w-full bg-[#F8F8F8] border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#55A14A] focus:border-transparent transition-colors"
                      placeholder="Enter your answer"
                    />
                  )}
                  {question.response_type === "multiple_choice" && (
                    <select
                      id={`answer-${question.id}`}
                      value={answers[question.id] || ""}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      className="block w-full bg-[#F8F8F8] border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#55A14A] focus:border-transparent transition-colors"
                    >
                      <option value="">Select an option</option>
                      {question.options.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                  {question.response_type === "boolean" && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`answer-${question.id}`}
                        checked={answers[question.id] === "true"}
                        onChange={(e) => handleAnswerChange(question.id, e.target.checked ? "true" : "false")}
                        className="w-5 h-5 text-[#55A14A] bg-[#F8F8F8] border-gray-200 rounded focus:ring-[#55A14A] focus:ring-2"
                      />
                      <label
                        htmlFor={`answer-${question.id}`}
                        className="ml-2 font-Inter font-[400] text-[16px] text-[#1C1C20]"
                      >
                        Yes
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-[24px] bg-[#F8F8F8] rounded-[12px] mt-5 p-4 justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`py-[6px] px-[30px] text-center text-white rounded-[12px] text-[18px] font-Inter font-[500] ${
              isSubmitting ? "bg-gray-300 cursor-not-allowed" : "bg-[#1C1C20] hover:bg-[#2A2A2A] transition-colors"
            }`}
          >
            {isSubmitting ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              "Save Answers"
            )}
          </button>
        </div>
      </form>
    </>
  );
}

export default DrawingQuestions;